import React, { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Smile, X } from 'lucide-react';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import MentionTextarea from '@/components/mentions/MentionTextarea';

interface CommentReplyInputProps {
  commentId: string;
  onSubmit: (
    commentId: string,
    content: string,
    mentions?: { full_name: string; auth_id: string }[]
  ) => Promise<void>;
  onCancel: () => void;
  placeholder?: string;
  autoFocus?: boolean;
}

const CommentReplyInput: React.FC<CommentReplyInputProps> = ({
  commentId,
  onSubmit,
  onCancel,
  placeholder = 'Write a reply...',
  autoFocus = true
}) => {
  const [content, setContent] = useState('');
  const [mentions, setMentions] = useState<{ full_name: string; auth_id: string }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const { isDarkMode } = useTheme();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSubmit(commentId, content, mentions);
      setContent('');
      setMentions([]);
    } catch (error) {
      console.error('Error submitting reply:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPosition = textarea.selectionStart || content.length;
    const textBeforeCursor = content.substring(0, cursorPosition);
    const textAfterCursor = content.substring(cursorPosition);

    const newValue = textBeforeCursor + emojiData.emoji + textAfterCursor;
    setContent(newValue);

    setTimeout(() => {
      textarea.focus();
      const newCursorPosition = cursorPosition + emojiData.emoji.length;
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 10);

    setShowEmojiPicker(false);
  };

  return (
    <div className="mt-2 ml-8 relative">
      <form onSubmit={handleSubmit} className="relative flex items-center">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowEmojiPicker(!showEmojiPicker);
          }}
          className={cn(
            "absolute left-2 transition-colors",
            showEmojiPicker ? "text-gold-400" : "text-dark-800 hover:text-gold-400"
          )}
        >
          <Smile className="h-4 w-4" />
        </button>

        <div className="flex-1 ml-6">
          <MentionTextarea
            id="reply-mention-textarea"
            onKeyDown={handleKeyDown}
            value={content}
            onChange={setContent}
            onMentionsChange={setMentions}
            placeholder={placeholder}
            disabled={isSubmitting}
            className={cn(
              "bg-dark-300 border border-dark-400 rounded-full px-4 py-1.5",
              "text-white text-sm placeholder-dark-800 w-full resize-none"
            )}
          />
        </div>

        <button
          type="submit"
          disabled={!content.trim() || isSubmitting}
          className="absolute right-2 text-gold-400 hover:text-gold-500 disabled:text-dark-800 transition-colors"
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </form>

      {showEmojiPicker && (
        <div
          className={cn(
            "absolute z-50 mt-2",
            "left-0 sm:left-auto sm:right-0",
            "w-full sm:w-auto"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative">
            <button
              onClick={() => setShowEmojiPicker(false)}
              className="absolute right-2 top-2 z-10 p-1 rounded-full bg-dark-300 text-white hover:bg-dark-400 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              autoFocusSearch={false}
              theme={isDarkMode ? Theme.DARK : Theme.LIGHT}
              searchPlaceHolder="Search emoji..."
              width="100%"
              height={350}
              lazyLoadEmojis
              previewConfig={{ showPreview: false }}
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
                maxWidth: '90vw',
                maxHeight: '80vh'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentReplyInput;
